import { CIVS } from '../../gameplay/civilisations';
import { kindIndex } from '../../gameplay/registry';
import { TICK_RATE } from '../../gameplay/stats';
import { generateMap } from '../../world/maps/generator';
import { MAP_SIZES, type GameMap, type MapSize, type MapType } from '../../world/maps/mapTypes';
import { AIController, type AIDifficulty, type AIPersonality } from '../../ai/aiController';
import type { CommandEnvelope } from './commands';
import { FX_HALF, FX_ONE } from './fixed';
import { applyCommand } from './systems/commands';
import { updateBehaviour } from './systems/behaviour';
import { updateCombat } from './systems/combat';
import { updateHealing } from './systems/healing';
import { updateConstruction } from './systems/construction';
import { updateCapture } from './systems/capture';
import { updateEconomy } from './systems/economy';
import { updateMovement } from './systems/movement';
import { updatePopulation, updateProduction, completeResearch } from './systems/production';
import { updateVictory, createVictoryState, DEFAULT_VICTORY, type VictoryConfig, type VictoryState } from './systems/victory';
import { updateVision } from './systems/vision';
import { updateGarrison } from './systems/garrison';
import { World } from './world';
import { ALL_TECHS } from '../../gameplay/registry';

/** Bumped whenever simulation rules change in a way that affects determinism. */
/**
 * Simulation rules version. Replays play back under the rules they were recorded with, so older
 * replays stay exact:
 *   1  path searches limited to 48 per tick
 *   2  path searches limited by total search steps per tick (no stutter on large group orders)
 *   3  human players' path searches go first, with extra steps of their own
 *   4  computer players choose whom to attack fairly (every enemy equally likely, human or not)
 *   5  engineers (University): commandeer enemy buildings, repair fast; the AI uses them
 *   6  forests are passable: units walk between the trees at 70% speed
 *   7  computer players: advance through every age, build the full range, try for a Wonder when
 *      that wins, and change strategy after losing a battle
 *   8  garrisons and transports: villagers shelter in the Town Centre (doubling its arrows) and board the
 *      Caravelle; villagers strike for 4 instead of 3
 *   9  a finished Wonder (and one tile around it) is visible to every player
 */
export const SIM_VERSION = 9;
/** Oldest rules this build can still play back. */
export const MIN_SIM_VERSION = 1;
export { TICK_RATE };
export const TICK_MS = 1000 / TICK_RATE;

/** The per-tick systems, in the order they run. */
const SYSTEMS: [string, (w: World) => void][] = [
  ['population', updatePopulation],
  ['behaviour', updateBehaviour],
  ['economy', updateEconomy],
  ['construction', updateConstruction],
  ['capture', updateCapture],
  ['production', updateProduction],
  ['movement', updateMovement],
  ['garrison', updateGarrison],
  ['combat', updateCombat],
  ['healing', updateHealing],
  ['vision', updateVision],
];

export const PLAYER_COLORS = [0x2f6fd8, 0xd83a2f, 0xe8c22a, 0x2fb04a, 0x9a3fd0, 0x2fc0c8, 0xe07a20, 0xd04a9a];

export interface PlayerConfig {
  name: string;
  civ: string;
  /** Team number; -1 for free-for-all (unique team). */
  team: number;
  color?: number;
  ai?: { difficulty: AIDifficulty; personality: AIPersonality } | null;
}

export type StartingResources = 'low' | 'standard' | 'high';
export const STARTING_RESOURCES: Record<StartingResources, number[]> = {
  low: [150, 150, 50, 100],
  standard: [300, 250, 100, 200],
  high: [1000, 1000, 1000, 500],
};

export interface GameConfig {
  seed: number;
  mapType: MapType;
  mapSize: MapSize;
  players: PlayerConfig[];
  startingAge: number;
  startingResources: StartingResources;
  popCapLimit: number;
  victory: VictoryConfig;
  revealMap?: boolean;
  /** Game clock speed relative to real time (Age of Empires runs at 1.7). Display only; the
   * simulation is the same at any speed. Missing in older saves, which played at 1. */
  gameSpeed?: number;
  /** Rules version to run under (replays set the one they were recorded with); missing = current. */
  simVersion?: number;
}

/** New games run their clock at 1.5x real time. */
export const DEFAULT_GAME_SPEED = 1.5;
export const GAME_SPEEDS: { value: number; name: string }[] = [
  { value: 1, name: 'Normal (1×)' },
  { value: 1.5, name: 'Fast (1.5×)' },
  { value: 1.7, name: 'Very fast (1.7×)' },
];

export function defaultConfig(partial: Partial<GameConfig> = {}): GameConfig {
  return {
    seed: 12345,
    mapType: 'continental',
    mapSize: 'small',
    players: [
      { name: 'Player', civ: 'english', team: -1 },
      { name: 'Computer', civ: 'french', team: -1, ai: { difficulty: 'standard', personality: 'balanced' } },
    ],
    startingAge: 0,
    gameSpeed: DEFAULT_GAME_SPEED,
    startingResources: 'standard',
    popCapLimit: 200,
    victory: { ...DEFAULT_VICTORY },
    ...partial,
  };
}

/**
 * The authoritative deterministic simulation. Advances in fixed ticks; all
 * player input arrives as scheduled CommandEnvelopes.
 */
export class Simulation {
  readonly config: GameConfig;
  readonly world: World;
  readonly map: GameMap;
  readonly ais: (AIController | null)[] = [];
  victoryState: VictoryState;
  private scheduled = new Map<number, CommandEnvelope[]>();
  /** Every command applied, in execution order (for replays). */
  readonly commandLog: CommandEnvelope[] = [];
  /** Hashes recorded every HASH_INTERVAL ticks. */
  readonly hashes = new Map<number, number>();
  static readonly HASH_INTERVAL = 20;
  /** Called with each tick's events after the tick completes (rendering/audio/UI). */
  onEvents: ((events: World['events']) => void) | null = null;
  /** When set, receives each system's time for every tick (profiling; off by default). */
  profile: ((system: string, ms: number) => void) | null = null;

  constructor(config: GameConfig) {
    this.config = config;
    const n = config.players.length;
    this.map = generateMap({ size: MAP_SIZES[config.mapSize], type: config.mapType, seed: config.seed, players: n });
    this.world = new World(this.map, config.seed ^ 0x1234567);
    this.world.rules = config.simVersion ?? SIM_VERSION;
    this.setupPlayers();
    this.setupWorld();
    const c = this.map.size * FX_HALF;
    this.victoryState = createVictoryState(config.victory.conditions.includes('territorial')
      ? [{ x: c, y: c }, { x: c - 12 * FX_ONE, y: c + 6 * FX_ONE }, { x: c + 12 * FX_ONE, y: c - 6 * FX_ONE }]
      : []);
    updateVision(this.world, true);
    if (config.revealMap) for (const v of this.world.visibility) v.fill(1);
  }

  private setupPlayers(): void {
    const res = STARTING_RESOURCES[this.config.startingResources];
    this.config.players.forEach((pc, i) => {
      const team = pc.team >= 0 ? pc.team : 100 + i;
      const civ = CIVS.find((c) => c.key === pc.civ) ?? CIVS[0];
      const r = res.slice();
      if (civ.startBonus) {
        r[0] += civ.startBonus.food ?? 0;
        r[1] += civ.startBonus.wood ?? 0;
        r[2] += civ.startBonus.gold ?? 0;
        r[3] += civ.startBonus.stone ?? 0;
      }
      this.world.addPlayer({ name: pc.name, civ: civ.key, team, color: pc.color ?? PLAYER_COLORS[i % PLAYER_COLORS.length], isAI: !!pc.ai, res: r });
      this.ais.push(pc.ai ? new AIController(i, pc.ai.difficulty, pc.ai.personality, this.config.seed + i * 7919) : null);
    });
  }

  private setupWorld(): void {
    const w = this.world;
    for (const r of this.map.resources) w.spawnResource(kindIndex(r.kind), r.tx, r.ty);
    const tc = kindIndex('townCenter');
    const vil = kindIndex('villager');
    this.map.starts.forEach((s, i) => {
      if (i >= w.players.length) return;
      const p = w.players[i];
      // Starting age: grant age-up technologies.
      for (let a = 1; a <= this.config.startingAge; a++) {
        const ti = ALL_TECHS.findIndex((t) => t.advancesTo === a);
        if (ti >= 0) completeResearch(w, p, ti);
      }
      w.events.length = 0;
      const tx = s.tx - 2;
      const ty = s.ty - 2;
      // Clear any resources in the footprint (generator reserves it, but be safe).
      w.spawnBuilding(tc, i, tx, ty, true);
      const cx = s.tx << 10;
      const cy = s.ty << 10;
      const offsets = [[-3, 3], [0, 3.2], [3, 3], [-3.2, 0], [-3.2, -2.5]];
      for (const [ox, oy] of offsets) w.spawn(vil, i, cx + Math.round(ox * FX_ONE), cy + Math.round(oy * FX_ONE));
      const scoutKey = p.stats[kindIndex('scout')].enabled ? 'scout' : 'eagleScout';
      w.spawn(kindIndex(scoutKey), i, cx + 3 * FX_ONE + 300, cy - 3 * FX_ONE);
    });
    updatePopulation(w);
  }

  /** Schedule a command. Duplicate (player, seq) pairs are ignored. */
  schedule(env: CommandEnvelope): boolean {
    if (env.tick <= this.world.tick) return false;
    const list = this.scheduled.get(env.tick) ?? [];
    if (list.some((e) => e.player === env.player && e.seq === env.seq)) return false;
    list.push(env);
    this.scheduled.set(env.tick, list);
    return true;
  }

  scheduledCommands(): CommandEnvelope[][] {
    return [...this.scheduled.values()];
  }

  restoreScheduled(list: CommandEnvelope[]): void {
    this.scheduled.clear();
    for (const c of list) {
      const l = this.scheduled.get(c.tick) ?? [];
      l.push(c);
      this.scheduled.set(c.tick, l);
    }
  }

  get tick(): number {
    return this.world.tick;
  }

  /** Advance one simulation tick. */
  step(): void {
    const w = this.world;
    if (w.gameOver) return;
    w.tick++;
    w.events = [];
    const cmds = this.scheduled.get(w.tick);
    if (cmds) {
      this.scheduled.delete(w.tick);
      cmds.sort((a, b) => a.player - b.player || a.seq - b.seq);
      for (const c of cmds) {
        const p = w.players[c.player];
        if (!p || c.seq <= p.lastSeq) continue; // out-of-order or duplicate
        p.lastSeq = c.seq;
        applyCommand(w, c);
        this.commandLog.push(c);
      }
    }
    const prof = this.profile;
    if (prof) {
      for (const [name, fn] of SYSTEMS) {
        const t = performance.now();
        fn(w);
        prof(name, performance.now() - t);
      }
      const t = performance.now();
      for (const ai of this.ais) if (ai) ai.update(this);
      prof('ai', performance.now() - t);
    } else {
      for (const [, fn] of SYSTEMS) fn(w);
      // AI decisions produce commands for a future tick (same path as human input).
      for (const ai of this.ais) if (ai) ai.update(this);
    }
    updateVictory(w, this.config.victory, this.victoryState);
    if (w.tick % Simulation.HASH_INTERVAL === 0) this.hashes.set(w.tick, this.hash());
    this.onEvents?.(w.events);
  }

  /** FNV-1a hash over authoritative state. */
  hash(): number {
    const w = this.world;
    let h = 0x811c9dc5;
    const mix = (v: number) => {
      h ^= v & 0xffff;
      h = Math.imul(h, 0x01000193);
      h ^= (v >>> 16) & 0xffff;
      h = Math.imul(h, 0x01000193);
    };
    mix(w.tick);
    mix(w.rng.state);
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s]) continue;
      mix(s);
      mix(w.kind[s]);
      mix(w.owner[s]);
      mix(w.x[s]);
      mix(w.y[s]);
      mix(w.hp[s]);
      mix(w.order[s]);
      mix(w.target[s]);
      mix(w.resAmt[s]);
      mix(w.carryAmt[s]);
      mix(w.buildProg[s]);
    }
    for (const p of w.players) {
      for (const r of p.res) mix(r);
      mix(p.age);
      mix(p.pop);
    }
    return h >>> 0;
  }
}

export const SAVE_VERSION = 1;

export interface SaveData {
  saveVersion: number;
  simVersion: number;
  config: GameConfig;
  world: Record<string, unknown>;
  ais: (unknown | null)[];
  victory: { wonderSince: [number, number][]; points: { x: number; y: number; team: number }[]; territoryTeam: number; territorySince: number };
  scheduled: CommandEnvelope[];
  commandLog: CommandEnvelope[];
}

export class IncompatibleSaveError extends Error {}

/** Serialise the complete authoritative state. */
export function saveSimulation(sim: Simulation): SaveData {
  const scheduled: CommandEnvelope[] = [];
  for (const l of sim.scheduledCommands()) scheduled.push(...l);
  return {
    saveVersion: SAVE_VERSION,
    simVersion: SIM_VERSION,
    config: sim.config,
    world: sim.world.serialize(),
    ais: sim.ais.map((a) => (a ? a.toJSON() : null)),
    victory: { ...sim.victoryState, wonderSince: [...sim.victoryState.wonderSince] },
    scheduled,
    commandLog: sim.commandLog.slice(),
  };
}

/** Restore a simulation from save data; throws IncompatibleSaveError on version mismatch. */
export function loadSimulation(d: SaveData): Simulation {
  if (!d || typeof d !== 'object' || d.saveVersion === undefined) throw new IncompatibleSaveError('Not a valid save file.');
  if (d.saveVersion !== SAVE_VERSION) throw new IncompatibleSaveError(`Save format v${d.saveVersion} is not supported (expected v${SAVE_VERSION}).`);
  // Older saves load and carry on under the current rules (a save is a snapshot, not a recording).
  if (!(d.simVersion >= MIN_SIM_VERSION && d.simVersion <= SIM_VERSION)) throw new IncompatibleSaveError(`Save was made with simulation rules v${d.simVersion}; this build supports v${MIN_SIM_VERSION} to v${SIM_VERSION}.`);
  const sim = new Simulation(d.config);
  sim.world.deserialize(d.world);
  d.ais.forEach((a, i) => (sim.ais[i] = a ? AIController.fromJSON(a) : null));
  sim.victoryState = { ...d.victory, wonderSince: new Map(d.victory.wonderSince) };
  sim.restoreScheduled(d.scheduled);
  sim.commandLog.push(...d.commandLog);
  return sim;
}

/** Overwrite an existing simulation's state from save data (network resync). */
export function applySaveInPlace(sim: Simulation, d: SaveData): void {
  if (d.simVersion !== SIM_VERSION) throw new IncompatibleSaveError('Resync state has a different simulation version.');
  sim.world.deserialize(d.world);
  d.ais.forEach((a, i) => (sim.ais[i] = a ? AIController.fromJSON(a) : null));
  sim.victoryState = { ...d.victory, wonderSince: new Map(d.victory.wonderSince) };
  sim.restoreScheduled(d.scheduled);
}

/** Seconds of game time represented by a tick count. */
export const ticksToSeconds = (t: number): number => t / TICK_RATE;
